import { useMemo } from "react"
import { useNavigate, useParams } from "react-router-dom"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap"
import classnames from "classnames"

import Breadcrumbs from "components/Common/Breadcrumb"
import { CanView } from "components/Common/CanView"
import ThemePlaceholder from "components/Common/ThemePlaceholder"
import { ACCESS } from "constants/accessPermissions"
import { useAuth } from "hooks/authHook"
import { useVehicleImporter } from "hooks/useVehicleImporter"
import useUrlParams from "hooks/useUrlParams"

import AdditionalInfoCard from "./components/AdditionalInfoCard"
import AddressCard from "./components/AddressCard"
import AuthorizedPersonsCard from "./components/AuthorizedPersonsCard"
import ImporterBankAccountsCard from "./components/ImporterBankAccountsCard"
import ImporterInfoCard from "./components/ImporterInfoCard"
import ImporterProfileCard from "./components/ImporterProfileCard"
import ImporterSectionUpdate from "./components/ImporterSectionUpdate"
import ImporterStockContainer from "./tabs/ImporterStockContainer"
import LoanSummaryCard from "pages/AccountPages/AccountLoan/LoanSummaryCard"

const toMobileEntries = mobile =>
  (Array.isArray(mobile) ? mobile : []).map(number => ({ number }))

const toAddressEntries = address =>
  (Array.isArray(address) ? address : []).map(item => ({
    showroom_name: item?.showroom_name ?? "",
    address: item?.showroom_address ?? item?.address ?? "",
    email: item?.email ?? "",
    mobile: item?.mobile ?? "",
  }))

const toPersonEntries = staffs =>
  (Array.isArray(staffs) ? staffs : []).map(staff => ({
    name: staff?.name ?? "",
    designation: staff?.designation ?? "",
    mobiles: (Array.isArray(staff?.mobile) ? staff.mobile : [])
      .filter(Boolean)
      .join(", "),
  }))

const IMPORTER_BASE_TABS = ["overview"]

const ImporterView = () => {
  const { identifier: importerId } = useParams()
  const navigate = useNavigate()
  const { data, isLoading, refetch } = useVehicleImporter(importerId)
  const importer = data?.data || {}

  const { hasAnyPermission } = useAuth()
  const canViewStocks = hasAnyPermission([ACCESS.STOCK.VIEW_STOCK_LIST])
  const canViewLoans = hasAnyPermission([ACCESS.ACCOUNT_LOAN.VIEW_LIST])

  const [params, updateParams] = useUrlParams({ tab: "overview" })
  const allowedTabs = useMemo(() => {
    const tabs = [...IMPORTER_BASE_TABS]
    if (canViewStocks) tabs.push("vehicles")
    if (canViewLoans) tabs.push("loans")
    return tabs
  }, [canViewStocks, canViewLoans])
  const activeTab = allowedTabs.includes(params.tab) ? params.tab : "overview"

  document.title = `Importer${importer?.name ? ` | ${importer.name}` : ""}`

  const toggleTab = tab => {
    if (activeTab !== tab) updateParams({ tab })
  }

  const addNewImporter = () => {
    navigate(`/importer/add?ref=self`)
  }

  const hasImporterData = useMemo(() => {
    if (isLoading) return false
    return Object.keys(importer).length > 0
  }, [importer, isLoading])

  const tabs = useMemo(
    () => [
      { id: "overview", label: "Overview", icon: "bx bx-detail" },
      ...(canViewStocks
        ? [
            {
              id: "vehicles",
              label: "Vehicles",
              icon: "bx bx-car",
              count: importer?.total_stocks || 0,
            },
          ]
        : []),
      ...(canViewLoans
        ? [{ id: "loans", label: "Loans", icon: "bx bx-money" }]
        : []),
    ],
    [canViewStocks, canViewLoans, importer?.total_stocks]
  )

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title="Importer"
          breadcrumbItem="Profile"
          loading={isLoading}
        />

        <Row className="mb-4">
          <Col lg="8">
            {isLoading ? (
              <div className="d-flex align-items-center">
                <ThemePlaceholder
                  style={{ width: 200, height: 32, borderRadius: 4 }}
                  className="me-3"
                />
                <ThemePlaceholder
                  style={{ width: 100, height: 24, borderRadius: 4 }}
                />
              </div>
            ) : hasImporterData ? (
              <div className="d-flex align-items-center mb-3">
                <h4 className="mb-0 me-3">
                  {importer.name || "Unnamed Importer"}
                </h4>
                {importer?.slug && (
                  <Badge color="light" className="border">
                    {importer.slug}
                  </Badge>
                )}
              </div>
            ) : null}
          </Col>
          <Col lg="4" className="text-end">
            <CanView access={[ACCESS.VEHICLE_IMPORTER.CREATE_VEHICLE_IMPORTER]}>
              <Button color="primary" onClick={addNewImporter}>
                <i className="fas fa-plus me-1"></i>
                Add New Importer
              </Button>
            </CanView>
          </Col>
        </Row>

        {!isLoading && !hasImporterData ? (
          <Card>
            <CardBody className="text-center py-5">
              <i className="bx bx-building display-4 text-muted mb-3"></i>
              <h5>Importer not found</h5>
              <p className="text-muted mb-4">
                The importer you are looking for does not exist or has been
                deleted.
              </p>
              <Button color="primary" onClick={() => navigate("/importers")}>
                Back to Importers
              </Button>
            </CardBody>
          </Card>
        ) : (
          <Row>
            <Col lg="4" className="mb-4">
              <ImporterProfileCard importer={importer} loading={isLoading} />

              <ImporterInfoCard
                importer={importer}
                loading={isLoading}
                action={
                  <ImporterSectionUpdate
                    importer={importer}
                    title="General Information"
                    btnId={`edit-importer-general-${importer?.id || "view"}`}
                    btnName="Edit General Information"
                    onSaved={refetch}
                    transformPayload={payload => ({
                      ...payload,
                      mobile: (payload.mobile || [])
                        .map(entry => entry.number)
                        .filter(Boolean),
                    })}
                    fields={[
                      {
                        name: "name",
                        label: "Name",
                        type: "text",
                        value: importer?.name,
                        required: true,
                        col: 6,
                      },
                      {
                        name: "email",
                        label: "Email",
                        type: "email",
                        value: importer?.email,
                        col: 6,
                      },
                      {
                        name: "website",
                        label: "Website",
                        type: "text",
                        value: importer?.website,
                        col: 12,
                      },
                      {
                        name: "mobile",
                        label: "Mobile Numbers",
                        type: "repeater",
                        value: toMobileEntries(importer?.mobile),
                        col: 12,
                        subFields: [
                          {
                            name: "number",
                            label: "Mobile Number",
                            col: 12,
                          },
                        ],
                      },
                    ]}
                  />
                }
              />

              <AdditionalInfoCard importer={importer} loading={isLoading} />
            </Col>

            <Col lg="8">
              <Nav pills className="nav-pills-custom mb-4">
                {tabs.map(tab => (
                  <NavItem key={tab.id}>
                    <NavLink
                      className={classnames({ active: activeTab === tab.id })}
                      onClick={() => toggleTab(tab.id)}
                      style={{ cursor: "pointer" }}
                    >
                      <i className={`${tab.icon} me-2`}></i>
                      {tab.label}
                      {tab.count > 0 && (
                        <Badge color="danger" pill className="ms-2">
                          {tab.count}
                        </Badge>
                      )}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>

              <TabContent activeTab={activeTab}>
                <TabPane tabId="overview">
                  <Row>
                    <Col lg="12">
                      <AuthorizedPersonsCard
                        persons={importer?.staff}
                        loading={isLoading}
                        action={
                          <ImporterSectionUpdate
                            importer={importer}
                            title="Authorized Persons"
                            btnId={`edit-importer-persons-${
                              importer?.id || "view"
                            }`}
                            btnName="Edit Authorized Persons"
                            onSaved={refetch}
                            fields={[
                              {
                                name: "staff",
                                label: "Authorized Persons",
                                type: "repeater",
                                value: toPersonEntries(importer?.staff),
                                col: 12,
                                subFields: [
                                  { name: "name", label: "Name", col: 6 },
                                  {
                                    name: "designation",
                                    label: "Designation",
                                    col: 6,
                                  },
                                  {
                                    name: "mobiles",
                                    label: "Mobile Numbers",
                                    col: 12,
                                    helpText:
                                      "Separate multiple numbers with commas.",
                                  },
                                ],
                              },
                            ]}
                          />
                        }
                      />
                    </Col>
                    <Col lg="12">
                      <AddressCard
                        addresses={importer?.address}
                        loading={isLoading}
                        action={
                          <ImporterSectionUpdate
                            importer={importer}
                            title="Addresses"
                            btnId={`edit-importer-address-${
                              importer?.id || "view"
                            }`}
                            btnName="Edit Addresses"
                            onSaved={refetch}
                            fields={[
                              {
                                name: "address",
                                label: "Addresses",
                                type: "repeater",
                                value: toAddressEntries(importer?.address),
                                col: 12,
                                subFields: [
                                  {
                                    name: "showroom_name",
                                    label: "Branch Name",
                                    col: 6,
                                  },
                                  {
                                    name: "address",
                                    label: "Address",
                                    type: "textarea",
                                    col: 12,
                                  },
                                  {
                                    name: "email",
                                    label: "Email",
                                    col: 6,
                                  },
                                  {
                                    name: "mobile",
                                    label: "Mobile",
                                    col: 6,
                                  },
                                ],
                              },
                            ]}
                          />
                        }
                      />
                    </Col>
                    <Col lg="12">
                      <ImporterBankAccountsCard
                        importer={importer}
                        loading={isLoading}
                        onChanged={refetch}
                      />
                    </Col>
                  </Row>
                </TabPane>

                {canViewStocks ? (
                  <TabPane tabId="vehicles">
                    {activeTab === "vehicles" ? (
                      <ImporterStockContainer importerId={importerId} />
                    ) : null}
                  </TabPane>
                ) : null}

                {canViewLoans ? (
                  <TabPane tabId="loans">
                    {activeTab === "loans" && importer?.id ? (
                      <LoanSummaryCard borrowerType="importer" borrowerId={importer.id} />
                    ) : null}
                  </TabPane>
                ) : null}
              </TabContent>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  )
}

export default ImporterView
